/* import __COLOCATED_TEMPLATE__ from './new-settings.hbs'; */
/* RESPONSIBLE TEAM: team-omni */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import type AppService from 'embercom/services/app-service';
import type NotifySlackChannel from 'embercom/models/operator/visual-builder/step/notify-slack-channel';
import { get } from 'embercom/lib/ajax';
import type IntlService from 'ember-intl/services/intl';

interface Arguments {
  step: NotifySlackChannel;
  isReadOnly: boolean;
}

interface SlackChannel {
  id: string;
  channel_id: string;
  channel_name: string;
  slack_team_name: string;
  channel_type: 'public_channel' | 'private_channel';
  channel_purpose: string;
}

interface ChannelDropdownItem {
  text: string;
  value: string;
}

interface IntegrationGroup {
  heading: string;
  items: ChannelDropdownItem[];
}

type SlackChannelsResponse = SlackChannel[];

export default class NewSettings extends Component<Arguments> {
  @service declare appService: AppService;
  @service declare intl: IntlService;

  @tracked channels: SlackChannel[] = [];
  @tracked selectedChannelId = '';
  @tracked error: string | null = null;

  constructor(owner: unknown, args: Arguments) {
    super(owner, args);
    taskFor(this.fetchSlackChannels).perform();
  }

  get appId() {
    return this.appService.app.id;
  }

  get isLoading() {
    return taskFor(this.fetchSlackChannels).isRunning;
  }

  get notificationChannels(): SlackChannel[] {
    return this.channels.filter((channel) => channel.channel_purpose === 'notification_only');
  }

  get groupedChannels(): IntegrationGroup[] {
    let groupsMap = new Map<string, SlackChannel[]>();

    this.notificationChannels.forEach((channel) => {
      let teamName = channel.slack_team_name;
      if (!groupsMap.has(teamName)) {
        groupsMap.set(teamName, []);
      }
      groupsMap.get(teamName)?.push(channel);
    });

    return Array.from(groupsMap.entries()).map(([teamName, teamChannels]) => ({
      heading: teamName,
      items: teamChannels.map((channel) => ({
        text: `#${channel.channel_name}`,
        value: channel.id,
      })),
    }));
  }

  get allChannelValues(): string[] {
    return this.notificationChannels.map((channel) => channel.id);
  }

  get allChannels(): ChannelDropdownItem[] {
    return this.notificationChannels.map((channel) => ({
      text: `#${channel.channel_name} (${channel.slack_team_name})`,
      value: channel.id,
    }));
  }

  get selectedChannelDisplay(): string {
    if (!this.selectedChannelId) {
      return '';
    }

    let selectedChannel = this.notificationChannels.find(
      (channel) => channel.id === this.selectedChannelId,
    );

    return selectedChannel ? `#${selectedChannel.channel_name}` : '';
  }

  get safeSelectedValue(): string | undefined {
    if (this.channels.length === 0 || !this.selectedChannelId) {
      return undefined;
    }

    return this.allChannelValues.includes(this.selectedChannelId)
      ? this.selectedChannelId
      : undefined;
  }

  @task({ drop: true })
  *fetchSlackChannels(): TaskGenerator<void> {
    try {
      this.error = null;
      let params = {
        app_id: this.appId,
        'includes[]': 'slack_integration',
      };

      let data: SlackChannelsResponse = yield get('/ember/slack/v2/channels', params);

      this.channels = data;
      this.initializeSelectedChannel();
    } catch (err: any) {
      this.error =
        'operator.workflows.visual-builder.notify-slack-channel.new-settings.generic-error';
    }
  }

  private initializeSelectedChannel(): void {
    if (this.channels.length === 0) {
      return;
    }

    let actionData = this.args.step.action.actionData;

    if (actionData?.intercom_slack_channel_id) {
      let channel = this.findChannelById(actionData.intercom_slack_channel_id);
      if (channel) {
        this.selectedChannelId = channel.id;
        return;
      }
    }

    if (actionData?.slack_channel_id) {
      let channel = this.findChannelBySlackId(actionData.slack_channel_id);
      if (channel) {
        this.selectedChannelId = channel.id;
        this.updateStepActionData({
          ...actionData,
          intercom_slack_channel_id: channel.id,
        });
        return;
      }
    }
    this.selectedChannelId = '';
  }

  private findChannelById(channelId: string): SlackChannel | undefined {
    return this.channels.find((channel) => channel.id === channelId);
  }

  private findChannelBySlackId(slackChannelId: string): SlackChannel | undefined {
    return this.channels.find((channel) => channel.channel_id === slackChannelId);
  }

  private updateStepActionData(actionData: any): void {
    this.args.step.action.actionData = actionData;
  }

  @action
  handleChannelChange(intercomChannelId: string): void {
    this.selectedChannelId = intercomChannelId;
    let selectedChannel = this.findChannelById(intercomChannelId);
    if (selectedChannel) {
      this.updateStepActionData({
        ...this.args.step.action.actionData,
        slack_channel_id: selectedChannel.channel_id,
        intercom_slack_channel_id: selectedChannel.id,
        slack_channel_name: `#${selectedChannel.channel_name}`,
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::NotifySlackChannel::NewSettings': typeof NewSettings;
    'workflows/graph-editor/node-items/steps/notify-slack-channel/new-settings': typeof NewSettings;
  }
}
